import {compare} from 'natural-orderby'

import {ROOT_PROJECT_ID} from '../../../../types'
import {emptyArray} from '../../../../utils/empty'

import type {TreeExpandState} from './AgentPoolPage.hooks'
import type {
  AgentPoolProjectsTreeProjectItem,
  AgentPoolProjectsTreeProjectNode,
  FlatProjectsTree,
  ProjectsConnectionEdges,
} from './AgentPoolPage.types'
import {insertSorted} from './AgentPoolPage.utils'

const STRING_ROOT_PROJECT_ID = '_Root'
type Matcher = (arg0: AgentPoolProjectsTreeProjectItem, arg1: boolean) => boolean

const naturalCompare = compare()

interface Namewise {
  name: string
}

function compareItemsByName<T extends Namewise>({name: name1}: T, {name: name2}: T): number {
  return naturalCompare(name1, name2)
}

export function createProjectsTree(
  projectEdges: ProjectsConnectionEdges,
): AgentPoolProjectsTreeProjectNode {
  const projectsHash: Record<string, AgentPoolProjectsTreeProjectNode> = {
    [STRING_ROOT_PROJECT_ID]: {
      id: STRING_ROOT_PROJECT_ID,
      rawId: STRING_ROOT_PROJECT_ID,
      name: 'Root',
      archived: false,
      children: emptyArray,
      agentPools: emptyArray,
    },
  }
  projectEdges.forEach(({node: projectNode}) => {
    if (
      projectNode.name == null ||
      projectNode.archived == null ||
      projectNode.agentPools == null ||
      projectNode.ancestorProjects == null
    ) {
      return
    }

    const {id, rawId, name, archived, permissions, ancestorProjects} = projectNode
    let projectTreeNode: AgentPoolProjectsTreeProjectNode | null = {
      id,
      rawId,
      name,
      permissions,
      archived,
      children: projectsHash[rawId] == null ? [] : projectsHash[rawId].children,
      // it can be already filled
      agentPools: projectNode.agentPools.edges.map(({node: agentPoolNode}) => ({
        ...agentPoolNode,
      })),
    }

    // it was created from the ancestorProjects list
    if (projectsHash[projectNode.rawId] != null) {
      const {id: ni, name: nn, archived: na, ...rest} = projectTreeNode
      // we should replace it because there's less data in ancestorProjects list
      Object.assign(projectsHash[projectNode.rawId], rest)
    } else {
      projectsHash[projectNode.rawId] = projectTreeNode
      ancestorProjects.edges.forEach(({node: ancestorProjectNode}) => {
        if (ancestorProjectNode.name == null || ancestorProjectNode.archived == null) {
          return
        }

        if (projectsHash[ancestorProjectNode.rawId] == null) {
          projectTreeNode = projectsHash[ancestorProjectNode.rawId] = {
            id: ancestorProjectNode.id,
            rawId: ancestorProjectNode.rawId,
            name: ancestorProjectNode.name,
            archived: ancestorProjectNode.archived,
            children: projectTreeNode != null ? [projectTreeNode] : [],
            agentPools: emptyArray,
          }
        } else {
          if (projectTreeNode != null) {
            projectsHash[ancestorProjectNode.rawId].children = insertSorted(
              projectsHash[ancestorProjectNode.rawId].children,
              projectTreeNode,
              compareItemsByName,
            )
          }
          projectTreeNode = null
        }
      })
    }
  })
  return projectsHash[STRING_ROOT_PROJECT_ID]
}

function flattenProjectsTreeNode(
  item: AgentPoolProjectsTreeProjectNode,
  agentPoolRawId: string,
  level: number,
): AgentPoolProjectsTreeProjectItem {
  return {
    id: item.id,
    rawId: item.rawId,
    name: item.name,
    archived: item.archived,
    permissions: item.permissions,
    agentPools: item.agentPools,
    assigned: Boolean(item.agentPools.find(({rawId}) => rawId === agentPoolRawId)),
    matching: false,
    containsMatching: false,
    level,
    expanded: false,
    expandable: item.children.length > 0,
    children: [],
  }
}

export function getFlatProjectsTree(
  treeRoot: AgentPoolProjectsTreeProjectNode,
  expandState: TreeExpandState,
  agentPoolRawId: string,
  match: Matcher,
  level = 0,
  parentIsMatching = false,
): FlatProjectsTree {
  const flatTreeRoot = flattenProjectsTreeNode(treeRoot, agentPoolRawId, level)
  // Expand state set by user
  flatTreeRoot.expanded = expandState[treeRoot.rawId]
  // Matching prop calculated based on the external func
  flatTreeRoot.matching = match(flatTreeRoot, parentIsMatching)
  let containsAssigned = false
  const flatChildSubtree = treeRoot.children.flatMap(child => {
    const subtree = getFlatProjectsTree(
      child,
      expandState,
      agentPoolRawId,
      match,
      level + 1,
      parentIsMatching || flatTreeRoot.matching,
    )

    const childItem = subtree[0]

    if (childItem != null) {
      childItem.parent = flatTreeRoot
      flatTreeRoot.children.push(childItem)

      if (childItem.assigned) {
        containsAssigned = true
      }

      if (childItem.matching || childItem.containsMatching) {
        flatTreeRoot.containsMatching = true
      }
    }

    return subtree
  })

  // Default expand state set by the app
  if (flatTreeRoot.expanded == null && (containsAssigned || flatTreeRoot.containsMatching)) {
    flatTreeRoot.expanded = true
  }

  const {matching, containsMatching, expanded} = flatTreeRoot
  return [
    ...(matching || containsMatching ? [flatTreeRoot] : []),
    ...(expanded === true ? flatChildSubtree : []),
  ]
}

interface ContainedItem<T> {
  rawId: string
  parent?: T | undefined
}

/**
 * Walks from the given item up through the tree
 * @param parent
 * @param callback
 */
export function walkItemAncestors<T extends ContainedItem<T>>(
  parent: T | null | undefined,
  callback: (item: T) => void,
) {
  if (parent != null && parent.rawId !== ROOT_PROJECT_ID) {
    callback(parent)
    walkItemAncestors(parent.parent, callback)
  }
}

interface ContainingItem<T> {
  rawId: string
  children: Array<T>
}

/**
 * Walks from the given item down through the tree
 * @param item
 * @param callback
 */
export function walkItemAndDescendants<T extends ContainingItem<T>>(
  item: T | null | undefined,
  callback: (item: T) => void,
) {
  if (item != null) {
    callback(item)
    for (let i = 0, l = item.children.length; i < l; i++) {
      walkItemAndDescendants(item.children[i], callback)
    }
  }
}

/**
 * Create new subtree of the item. Each node is cunstructed in the callback
 */
export function mapItemWithSubtree<T extends ContainingItem<T>>(
  item: T,
  callback: (item: T) => T,
): T {
  const modifiedItem = callback(item)
  return {
    ...modifiedItem,
    children: modifiedItem?.children.map(childItem => mapItemWithSubtree(childItem, callback)),
  }
}
